import React from 'react';
import { useLocation } from 'react-router-dom';
import { SectionItemData } from '../../../data/datatypes';
import { useAppDispatch } from '../../../store/hooks';
import { setCurrentExerciseUrl } from '../../../store/moduleStateSlice';
import Game from '../Game';
import Page from '../Page/Page';
import { getUrlWithTransientStateProperties } from '../Sessions/Session.logic';
import MySwiper from '../Swiper/MySwiper';
import { SectionNavBarButtonOptions, SectionNavBarOptions } from '../../atoms/SectionNavButton.types';
import SectionNavButtonsBar from '../../molecules/SectionNavButtonsBar';

type Properties = {
    item: SectionItemData;
    sectionId: `section_${string}`;
    itemId: `item_${string}`;
    onNextButtonClick: () => void;
    onBackButtonClick?: () => void;
    nextButtonLabel: string;
};

const SectionItem = (props: Properties) => {
    const { item } = props;
    const [nextButtonDisabled, setNextButtonDisabled] = React.useState<boolean>(false);
    const [nextButtonLabel, setNextButtonLabel] = React.useState<string>();
    const [navBarLeftButtonOptions, setNavBarLeftButtonOptions] = React.useState<SectionNavBarButtonOptions>();
    const [navBarRightButtonOptions, setNavBarRightButtonOptions] = React.useState<SectionNavBarButtonOptions>();

    const dispatch = useAppDispatch();
    const pathname = useLocation().pathname;

    // TODO Maybe move in Page.tsx? Combine with saving page state
    React.useEffect(() => {
        dispatch(setCurrentExerciseUrl(getUrlWithTransientStateProperties(pathname)));
    }, [pathname]);

    const navBarButtonProps: SectionNavBarOptions = {
        rightButtonOptions: navBarRightButtonOptions || {
            text: nextButtonLabel || props.nextButtonLabel,
            onClick: () => { props.onNextButtonClick(); },
            disabled: nextButtonDisabled
        }
    };
    if (props.onBackButtonClick !== undefined || navBarLeftButtonOptions !== undefined) {
        navBarButtonProps.leftButtonOptions = navBarLeftButtonOptions || {
            text: 'Zurück',
            onClick: () => { 
                if (props.onBackButtonClick !== undefined) {
                    props.onBackButtonClick();
                } else {
                    return;
                }
            }
        };
    }

    const leftSwipeDisabled = nextButtonDisabled || props.nextButtonLabel !== 'Weiter';
    const nextSwipeCallback = React.useMemo(() => {
        if (leftSwipeDisabled) {
            return undefined;
        } else {
            return () => props.onNextButtonClick();
        }
    }, [nextButtonDisabled, props.nextButtonLabel, props.onNextButtonClick]);

    const content = React.useMemo(() => {
        switch (item.type) {
            case 'page': 
                return (
                <Page
                    page={item}
                    sectionId={props.sectionId}
                    itemId={props.itemId}
                    setNextButtonDisabled={setNextButtonDisabled}
                />);
            case 'game': 
                return (
                <Game 
                    item={item}
                    setNextButtonLabel={setNextButtonLabel}
                    setNextButtonDisabled={setNextButtonDisabled}
                    setNavBarLeftButton={setNavBarLeftButtonOptions}
                    setNavBarRightButton={setNavBarRightButtonOptions}
                />);
            default: return <div>Unbekannes Sectionitem</div>;
        }
    }, [item, setNextButtonLabel, setNextButtonDisabled]);

    const disableScrolling = React.useMemo(() => {
        if (item.type === 'game') {
            return true;
        } else {
            return false;
        }
    }, [item.type]);

    return (
        <MySwiper
            key={pathname}
            resetKey={pathname}
            afterPrevSwipe={navBarButtonProps.leftButtonOptions ? () => navBarButtonProps.leftButtonOptions?.onClick() : undefined}
            afterNextSwipe={nextSwipeCallback}
            disableScrolling={disableScrolling}
        >
            {content}
            <SectionNavButtonsBar 
                leftButtonOptions={navBarButtonProps.leftButtonOptions}
                rightButtonOptions={navBarButtonProps.rightButtonOptions}
            />
        </MySwiper>
    );
};

export default SectionItem;
